import type { PipelineDqxRunOutcome, PipelineFileDqxState } from "@/lib/pipelineDqx";
import { aggregatePipelineDqxRuns } from "@/lib/pipelineDqx";

/**
 * Ordered runs for the multi-table DQ dialog. Falls back to the aggregate
 * primary when `runs` is missing (single-job / mapping-triggered DQ).
 */
export function listPipelineDqxDialogRuns(
  state: PipelineFileDqxState | null | undefined
): PipelineDqxRunOutcome[] {
  if (!state) return [];
  if (state.runs && state.runs.length > 0) return state.runs;
  if (!state.jobId) return [];
  return [
    {
      runId: state.runId ?? 0,
      jobId: state.jobId,
      phase: state.phase,
      rowsPassing: state.rowsPassing,
      rowsWithIssues: state.rowsWithIssues,
      totalRowsChecked: state.totalRowsChecked,
      message: state.message,
      error: state.error,
    },
  ];
}

export function clampPipelineDqxDialogRunIndex(index: number, runCount: number): number {
  if (runCount <= 0) return 0;
  return Math.max(0, Math.min(index, runCount - 1));
}

export function selectPipelineDqxDialogRun(
  runs: PipelineDqxRunOutcome[],
  index: number
): PipelineDqxRunOutcome | null {
  if (runs.length === 0) return null;
  return runs[clampPipelineDqxDialogRunIndex(index, runs.length)] ?? null;
}

/**
 * File-global source_row_index offset for the active multi-table DQ run.
 * Prefer explicit start from apply; else sum prior runs' totalRowsChecked.
 */
export function resolvePipelineDqxRowIndexBase(
  runs: Array<{
    sourceRowIndexStart?: number | null;
    totalRowsChecked?: number | null;
  }>,
  activeIndex: number
): number {
  if (runs.length === 0 || activeIndex <= 0) {
    const start = runs[activeIndex]?.sourceRowIndexStart;
    return typeof start === "number" && Number.isFinite(start) ? Math.max(0, start) : 0;
  }
  const active = runs[activeIndex];
  if (
    active &&
    typeof active.sourceRowIndexStart === "number" &&
    Number.isFinite(active.sourceRowIndexStart)
  ) {
    return Math.max(0, active.sourceRowIndexStart);
  }
  let base = 0;
  for (let i = 0; i < activeIndex && i < runs.length; i++) {
    const prior = runs[i];
    const n = prior?.totalRowsChecked;
    if (typeof n === "number" && Number.isFinite(n) && n > 0) {
      base += n;
    }
  }
  return base;
}

/** Replace one table's run (e.g. after cleanse rerun) and re-aggregate file state. */
export function replacePipelineDqxRunAtIndex(
  prior: PipelineFileDqxState,
  index: number,
  nextRun: PipelineDqxRunOutcome
): PipelineFileDqxState {
  const runs = listPipelineDqxDialogRuns(prior);
  if (runs.length === 0) {
    return aggregatePipelineDqxRuns([nextRun]);
  }
  const clamped = clampPipelineDqxDialogRunIndex(index, runs.length);
  const updated = runs.map((run, i) => (i === clamped ? { ...run, ...nextRun } : run));
  return aggregatePipelineDqxRuns(updated);
}
